/**
 * What a call log can say about one call beyond who it was with.
 *
 * Everything here reads evidence the routing bots already wrote while the call was live —
 * which menu option the caller chose, whose phone rang in what order, who picked up. None
 * of it is configured anywhere, so a log built on it cannot drift from what actually
 * happened.
 */

import { Communication, Practitioner, Reference } from '@medplum/fhirtypes';
import { getCommunicationDirection, getProviderRoutingHistory } from './communication';
import { CommunicationDirection } from './constants';
import { getIncomingCallAnswerer } from './voice-call-outcome';
import { getTwilioCallHistoryEntries } from './voice-call-outcome';

/**
 * `twilio-call-history` entry url `annotateCallsWithIvrInteraction` writes the caller's
 * menu choice under. Shared so the bot that writes it and the log that reads it cannot
 * drift apart.
 */
export const IVR_INTERACTION_HISTORY_URL = 'ivr-interaction';

/** One trip through the phone menu: which menu, and what the caller pressed. */
export interface IvrSelection {
  /** `business-hours-en`, `business-hours-es`, … */
  templateId: string;
  digit: number;
  timestamp?: string;
}

function decodeHistoryEntry<T>(data: string | undefined): T | undefined {
  if (!data) {
    return undefined;
  }
  try {
    const json = typeof Buffer !== 'undefined' ? Buffer.from(data, 'base64').toString('utf-8') : atob(data);
    return JSON.parse(json) as T;
  } catch {
    return undefined;
  }
}

/**
 * Which menu option this caller chose, or `undefined` if they never reached a menu.
 *
 * The **last** selection wins. A caller who presses the language-switch key gets a second
 * menu and a second interaction entry, and it is the one they acted on that decided where
 * the call went.
 */
export function getIvrSelection(communication: Communication): IvrSelection | undefined {
  const selections = getTwilioCallHistoryEntries(communication)
    .filter((entry) => entry.url === IVR_INTERACTION_HISTORY_URL)
    .map((entry) => decodeHistoryEntry<IvrSelection>(entry.valueAttachment?.data))
    .filter((selection): selection is IvrSelection => typeof selection?.digit === 'number');

  return selections[selections.length - 1];
}

/**
 * Everyone on our side whose phone this call rang, in the order it rang them.
 *
 * `getProviderRoutingHistory` is the same list the PAC report counts offers by: the
 * providers the call moved past, then whoever it was last with. Two corrections are applied
 * before it can be shown to somebody:
 *
 * **Only providers.** That function appends `recipient[0]` whatever it is, and on an
 * outgoing call `recipient` is the *patient* — so it reports the person who was called as
 * one of the people we rang. It is harmless where the report uses it, which is to test
 * whether a given coordinator is in the list, and wrong the moment the list is rendered.
 *
 * **Once each.** A call that collapses `recipient` onto the answerer names that person in
 * both places, and the same person on two rungs of one call reads as a routing bug that did
 * not happen.
 *
 * Empty on an outgoing call, and on an inbound call that never reached a provider — the
 * answering-service forwards and the hang-ups.
 */
export function getCallRoutingPath(communication: Communication): Reference<Practitioner>[] {
  const seen = new Set<string>();
  const path: Reference<Practitioner>[] = [];

  for (const provider of getProviderRoutingHistory(communication)) {
    // `getProviderRoutingHistory` casts every child's `valueReference` to a reference
    // without checking one is there, so an entry written under a different url — a skip
    // record, say — arrives here as `undefined`. Reading `.reference` off it throws
    // inside a table cell, which is a blank page rather than a blank cell.
    const key = provider?.reference;
    if (!key || seen.has(key) || !key.startsWith('Practitioner/')) {
      continue;
    }
    seen.add(key);
    path.push(provider);
  }

  return path;
}

/**
 * Stands in for a teammate nothing on the page can name.
 *
 * Not a blank cell — a row that rang somebody unnameable still rang somebody, and an empty
 * cell reads as "nobody". Not the reference either: the roster a log resolves names against
 * is a `PractitionerRole` search, so a teammate holding no role — a deactivated one, say —
 * is simply absent from it, and on staging that is 126 cells in 1500 rows. A 36-character
 * uuid in a table cell reads as broken data and overflows the column it is in.
 */
export const UNNAMED_PROVIDER_LABEL = 'Unknown teammate';

/**
 * A name for a provider reference, for a log row.
 *
 * References the routing bots wrote carry `display`; the ones that do not are resolved by
 * the caller against the practitioner directory it already holds. Falls back to
 * {@link UNNAMED_PROVIDER_LABEL} when neither names them.
 */
export function describeProviderReference(
  provider: Reference<Practitioner> | undefined,
  resolveName?: (reference: string) => string | undefined
): string | undefined {
  if (!provider) {
    return undefined;
  }

  const resolved = provider.reference ? resolveName?.(provider.reference) : undefined;

  return resolved ?? provider.display ?? UNNAMED_PROVIDER_LABEL;
}

/**
 * `IN` or `OUT` for a log row, or `undefined` for a row that does not say.
 *
 * Reads only the direction coding, so it serves texts as well as calls: the Phone logs and
 * the SMS logs both render it beside the type icon.
 *
 * The third state earns its keep. Staging holds phone calls carrying no direction coding at
 * all, whose `sender` is a Patient and whose `recipient` is a Practitioner — plainly
 * inbound. A two-state label has to guess on those, and the guess is printed as a fact
 * beside an icon that guesses the other way: `getCommunicationType` defaults an undirected
 * voice call to *incoming*. Better to say nothing than to have one row assert both.
 */
export function getCommunicationDirectionLabel(communication: Communication): 'IN' | 'OUT' | undefined {
  switch (getCommunicationDirection(communication)) {
    case CommunicationDirection.INCOMING:
      return 'IN';
    case CommunicationDirection.OUTGOING:
      return 'OUT';
    default:
      return undefined;
  }
}

/**
 * Which of our people was on this call, whichever way it went.
 *
 * The two directions record it in different places, because they are different facts:
 *
 * - **Inbound**, it is whoever picked up. `recipient` names the provider the call is
 *   *offered* to, so it only identifies an answerer on a call that reads as answered —
 *   anywhere else it names somebody who let the phone ring. `getIncomingCallAnswerer`
 *   applies that test.
 * - **Outbound**, it is whoever dialled: `sender`, set when the call is created and never
 *   moved. It stands whether or not the patient picked up, which is the point — the
 *   attempt belongs to the person who made it, and whether it connected is the outcome.
 *
 * Guarded on the reference type rather than assumed from the direction: an inbound partner
 * call has an `Organization` sender, and an outbound one has a `Patient` recipient.
 */
export function getCallTeammate(communication: Communication): Reference<Practitioner> | undefined {
  if (getCommunicationDirection(communication) === CommunicationDirection.INCOMING) {
    return getIncomingCallAnswerer(communication);
  }

  const sender = communication.sender;

  return sender?.reference?.startsWith('Practitioner/') ? (sender as Reference<Practitioner>) : undefined;
}

/**
 * A call length for a log row: `3:07`.
 *
 * Minutes and seconds with no hour field — the longest call on record is under six
 * minutes, and an `h:mm:ss` that reads `0:03:07` costs every row two characters to say
 * nothing.
 */
export function formatCallLength(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The menu a caller heard, for somebody reading a log: "English menu".
 *
 * `templateId` is an internal id (`business-hours-en`), and since BAR-2473 the only thing
 * that varies between the reachable templates is language — the hours are resolved per
 * option after a digit is pressed, not by which greeting played.
 */
export function describeIvrTemplate(templateId: string | undefined): string | undefined {
  if (!templateId) {
    return undefined;
  }
  if (templateId.endsWith('-es')) {
    return 'Spanish menu';
  }
  if (templateId.endsWith('-en')) {
    return 'English menu';
  }
  return templateId;
}
